import React, { PropsWithChildren, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import ReviewsFilterInput from '@app/model/review/input/ReviewsFilterInput';
import ReviewsContentFilterInput from '@app/model/review/input/ReviewsContentFilterInput';
import DataProvider from '@app/model/_common/DataProvider';
import ReviewsSorterType from '@app/model/review/input/ReviewsSorterType';
import { ReviewsQueryKeys } from '@app/model/review/service/ReviewsQueryKeys';
import ReviewOutput from '@app/model/review/output/ReviewOutput';
import { ReviewService } from '@app/model/review/service/ReviewService';
import Message from '@app/component/message/Message';
import MainPage from '@app/component/page/MainPage';
import _isEmpty from 'lodash/isEmpty';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import ReviewCategoryOutput from '@app/model/review/output/ReviewCategoryOutput';
import ReviewsPagePlaceholder from '@app/component/placeholder/ReviewsPagePlaceholder';

export type ReviewsTools = {
	reviewOutputs: ReviewOutput[]
	findCategoryName: (categoryId: number) => string
	findCategory: (categoryId: number) => ReviewCategoryOutput | undefined
	categories: ReviewCategoryOutput[]
	filter: ReviewsFilterInput;
	setFilter: (filter: ReviewsFilterInput) => void;
	reset: () => void;
	index: number;
	setIndex: (index: number) => void;
	next: () => void;
	prev: () => void;
	getSelectedReviewOutput: () => ReviewOutput | undefined;
}

const initFilter = (companyHotelId: number) => {
	return {
		companyHotelId: companyHotelId,
		content: ReviewsContentFilterInput.ALL,
		isAssignedToMe: false,
		months: 3,
		provider: undefined,
		sorter: ReviewsSorterType.DATE_DESC,
	};
};

export const ReviewsContext = React.createContext<ReviewsTools>({
	reviewOutputs: [],
	findCategoryName: () => '',
	findCategory: () => undefined,
	categories: [],
	filter: initFilter(-1),
	setFilter: () => {
	},
	reset: () => {
	},
	index: 0,
	setIndex: (index: number) => {
	},
	next: () => {
	},
	prev: () => {
	},
	getSelectedReviewOutput: () => undefined,
});

const ReviewsProvider: React.FunctionComponent<PropsWithChildren> = React.memo(({ children }) => {

	const [filter, setFilter] = useState<ReviewsFilterInput | undefined>(undefined);
	const { companyHotel } = useSelectedCompanyHotel();


	useEffect(() => {
		setFilter(initFilter(companyHotel.id));
	}, []);

	if (!filter)
		return <MainPage><ReviewsPagePlaceholder /></MainPage>;

	return <Provider companyHotelId={companyHotel.id} filter={filter} setFilter={setFilter}>
		{children}
	</Provider>;

});

const Provider: React.FunctionComponent<PropsWithChildren<{
	companyHotelId: number,
	filter: ReviewsFilterInput,
	setFilter: (filter: ReviewsFilterInput) => void
}>> = ({ children, companyHotelId, filter, setFilter }) => {

	const [index, setIndex] = useState<number>(0);

	useEffect(() => {
		setIndex(0);
	}, [filter]);

	const { data: reviews, isLoading, isFetching, isError } = useQuery({
		queryFn: () => ReviewService.filterReviews(filter),
		queryKey: ReviewsQueryKeys.filter(filter),
		refetchOnWindowFocus: false,
		refetchOnMount: true,
		refetchOnReconnect: false,
		staleTime: 0,
	});

	const { data: categories } = useQuery({
		queryFn: () => ReviewService.findReviewCategories(),
		queryKey: ReviewsQueryKeys.categories(),
		refetchOnWindowFocus: false,
		refetchOnMount: false,
		refetchOnReconnect: false,
	});

	const next = () => {
		if (!_isEmpty(reviews) && index < reviews!.length - 1) setIndex(index + 1);
	};

	const prev = () => {
		if (index > 0) setIndex(index - 1);
	};

	const getSelectedReview = () => {
		return reviews?.[index];
	};

	if (isLoading || isFetching)
		return <MainPage><ReviewsPagePlaceholder /></MainPage>;

	if (isError)
		return <MainPage><Message state="error">Reviews cannot be loaded at the moment. Please try again
			later!</Message></MainPage>;


	const findCategoryName = (categoryId: number): string => {
		if (_isEmpty(categories)) return 'Unknown';
		return categories!.find(c => c.id === categoryId)?.name ?? 'Unknown';
	};

	const findCategory = (categoryId: number): ReviewCategoryOutput | undefined => {
		return categories!.find(c => c.id === categoryId);
	};

	const reset = () => {
		setFilter(initFilter(companyHotelId));
	};

	return <ReviewsContext.Provider value={{
		reviewOutputs: reviews || [],
		findCategoryName: findCategoryName,
		findCategory: findCategory,
		categories: categories!,
		filter,
		setFilter,
		reset,
		index,
		setIndex,
		next,
		prev,
		getSelectedReviewOutput: getSelectedReview,
	}}>
		{children}
	</ReviewsContext.Provider>;
};

export default ReviewsProvider;
